// ArtifactsPage — the persistent library of everything Ros publishes (surveys, crosstabs, websites…).
// Search + sort + per-type filter chips; table↔card layout (persisted); each artifact is an expandable
// GROUP (parent = latest version, chevron reveals older versions as child rows / a card sublist). Every
// version carries its own retention (publishedAt + retention_days → ok/soon/urgent). Every artifact has
// two independent entry points: open it here, or open it beside its source/new chat.

import { useEffect, useMemo, useState, useCallback, useRef } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import IconButton from '@mui/material/IconButton'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import Menu from '@mui/material/Menu'
import Divider from '@mui/material/Divider'
import Paper from '@mui/material/Paper'
import Tooltip from '@mui/material/Tooltip'
import Snackbar from '@mui/material/Snackbar'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import SearchIcon from '@mui/icons-material/Search'
import TableRowsOutlinedIcon from '@mui/icons-material/TableRowsOutlined'
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import ScheduleIcon from '@mui/icons-material/Schedule'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import AutorenewIcon from '@mui/icons-material/Autorenew'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined'
import UploadIcon from '@mui/icons-material/Upload'
import { useRosShell } from '../RosShellContext'
import { useRosFeatures } from '../access/useRosFeatures'
import type { SessionArtifact, PanelArtifactVersion } from '../components/rosArtifacts'
import { AppArtifactBuilderCard } from '../components/AppArtifactBuilderCard'
import { fetchArtifacts, extendRetention, deleteArtifact } from './artifactsApi'
import {
  fetchAppArtifactBuilderSkill,
  fetchAppArtifactBuilderSkillZip,
  type AppArtifactBuilderSkillMetadata,
} from '../services/AppArtifactBuilderSkillService'
import {
  typeDef, TONE_COLOR, retentionFor, TIER_COLOR, formatBytes, relativeModified,
  flattenArtifacts, latestVersion, ART_SORTS, MAX_RETENTION_DAYS, DEFAULT_RETENTION_DAYS,
  type ArtSortId, type RetentionState,
} from './artifactsModel'
import { SERIF_HEADING } from '../theme'

const LAYOUT_KEY = 'ros.artifacts.layout'
const TOAST_MS = 4000

type Layout = 'table' | 'cards'

export function TypeTile({ type, size = 32 }: { type: string; size?: number }) {
  const def = typeDef(type)
  const color = TONE_COLOR[def.tone]
  const Icon = def.icon
  return (
    <Box sx={{
      width: size, height: size, borderRadius: 1.5, flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      bgcolor: `${color}14`, color,
    }}>
      <Icon sx={{ fontSize: size * 0.5 }} />
    </Box>
  )
}

export function TypeChip({ type }: { type: string }) {
  const def = typeDef(type)
  const color = TONE_COLOR[def.tone]
  return (
    <Box component="span" sx={{
      px: 0.9, py: 0.2, borderRadius: 1, border: '1px solid', borderColor: `${color}55`,
      color, bgcolor: `${color}0F`, fontSize: '0.7rem', fontWeight: 600, whiteSpace: 'nowrap',
    }}>{def.label}</Box>
  )
}

function RetentionChip({ r }: { r: RetentionState }) {
  const color = TIER_COLOR[r.tier]
  const Icon = r.tier === 'ok' ? ScheduleIcon : WarningAmberIcon
  return (
    <Tooltip title={`Expires ${new Date(r.expiresAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`}>
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, color, fontSize: '0.72rem', fontWeight: 500, whiteSpace: 'nowrap' }}>
        <Icon sx={{ fontSize: 14 }} /> {r.label}
      </Box>
    </Tooltip>
  )
}

export function ArtifactsPage() {
  const shell = useRosShell()
  const {enabled, authFetch} = useRosFeatures()
  const artifactsMgmtEnabled = enabled('artifacts-mgmt')
  const appArtifactsEnabled = enabled('app-artifacts')
  const [artifacts, setArtifacts] = useState<SessionArtifact[]>([])
  const [loading, setLoading] = useState(true)
  const [query, setQuery] = useState('')
  const [typeFilter, setTypeFilter] = useState<string>('all')
  const [sort, setSort] = useState<ArtSortId>('modified')
  const [layout, setLayout] = useState<Layout>(() => (localStorage.getItem(LAYOUT_KEY) as Layout) || 'table')
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [confirmDelete, setConfirmDelete] = useState<SessionArtifact | null>(null)
  const [toast, setToast] = useState<string | null>(null)
  const [importing, setImporting] = useState(false)
  const [builderDialogOpen, setBuilderDialogOpen] = useState(false)
  const importInputRef = useRef<HTMLInputElement>(null)
  const [builderSkill, setBuilderSkill] = useState<AppArtifactBuilderSkillMetadata | null>(null)
  const [builderLoading, setBuilderLoading] = useState(false)
  const [builderDownloading, setBuilderDownloading] = useState(false)
  const [builderError, setBuilderError] = useState<string | null>(null)

  useEffect(() => { localStorage.setItem(LAYOUT_KEY, layout) }, [layout])

  const reload = useCallback(async () => {
    try {
      const groups = await fetchArtifacts(shell.apiBaseUrl, shell.token, authFetch as typeof fetch)
      setArtifacts(flattenArtifacts(groups, artifactsMgmtEnabled, appArtifactsEnabled))
    } catch { /* surfaced as empty state */ }
    finally { setLoading(false) }
  }, [shell.apiBaseUrl, shell.token, artifactsMgmtEnabled, appArtifactsEnabled, authFetch])

  useEffect(() => { void reload() }, [reload])

  const loadBuilderSkill = useCallback(async () => {
    if (!appArtifactsEnabled) return
    setBuilderLoading(true)
    setBuilderError(null)
    try {
      setBuilderSkill(await fetchAppArtifactBuilderSkill(
        shell.apiBaseUrl,
        authFetch as typeof fetch,
      ))
    } catch (error) {
      setBuilderError(error instanceof Error ? error.message : 'Could not load App Artifact Builder')
    } finally {
      setBuilderLoading(false)
    }
  }, [appArtifactsEnabled, shell.apiBaseUrl, authFetch])

  const openAppArtifactUpload = () => {
    setBuilderDialogOpen(true)
    if (!builderSkill && !builderLoading) void loadBuilderSkill()
  }

  const downloadBuilderSkill = async () => {
    if (!builderSkill) return
    setBuilderDownloading(true)
    try {
      const {blob, fileName} = await fetchAppArtifactBuilderSkillZip(
        shell.apiBaseUrl,
        builderSkill.fileName,
        authFetch as typeof fetch,
      )
      const objectUrl = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = objectUrl
      anchor.download = fileName
      document.body.appendChild(anchor)
      anchor.click()
      document.body.removeChild(anchor)
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000)
      setToast(`Downloading ${builderSkill.name} v${builderSkill.version}…`)
    } catch (error) {
      setToast(error instanceof Error ? error.message : 'App Artifact Builder download failed.')
    } finally {
      setBuilderDownloading(false)
    }
  }

  const importAppArtifact = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    setBuilderDialogOpen(false)
    setImporting(true)
    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/octet-stream' }
      if (shell.token) headers.Authorization = `Bearer ${shell.token}`
      const response = await authFetch(`${shell.apiBaseUrl}/api/r3/app-artifacts/import`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: file,
      })
      const result = await response.json().catch(() => ({})) as {
        displayName?: string
        error?: string
        details?: string[]
      }
      if (!response.ok) {
        throw new Error(result.details?.join('; ') || result.error || `Upload failed (${response.status})`)
      }
      await reload()
      setToast(`Uploaded “${result.displayName || file.name}” as an App Artifact.`)
    } catch (error) {
      setToast(error instanceof Error ? error.message : 'App Artifact upload failed.')
    } finally {
      setImporting(false)
    }
  }

  const counts = useMemo(() => {
    const m: Record<string, number> = {}
    for (const a of artifacts) m[a.type] = (m[a.type] ?? 0) + 1
    return m
  }, [artifacts])
  const presentTypes = useMemo(() => Object.keys(counts).sort(), [counts])

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase()
    const list = artifacts.filter(a =>
      (typeFilter === 'all' || a.type === typeFilter) &&
      (q === '' || a.title.toLowerCase().includes(q)),
    )
    if (sort === 'name') list.sort((a, b) => a.title.localeCompare(b.title))
    else if (sort === 'modified') list.sort((a, b) => Date.parse(latestVersion(b).publishedAt) - Date.parse(latestVersion(a).publishedAt))
    else if (sort === 'expiring') list.sort((a, b) => retentionFor(latestVersion(a)).daysLeft - retentionFor(latestVersion(b)).daysLeft)
    return list
  }, [artifacts, query, typeFilter, sort])

  const toggleExpand = (id: string) => setExpanded(prev => {
    const next = new Set(prev)
    if (next.has(id)) next.delete(id); else next.add(id)
    return next
  })

  const openInChat = (artifact: SessionArtifact, version?: PanelArtifactVersion) => {
    void shell.openArtifactInChat(artifact, version ? version.sessionId : artifact.sessionId)
      .catch(() => setToast('Could not open a chat for this artifact.'))
  }

  const openArtifact = (artifact: SessionArtifact) => {
    // Ros auth is intentionally held in sessionStorage. A same-origin tab must be opened with an
    // opener long enough for the browser to clone that storage; sever it immediately afterward.
    const opened = window.open(`/artifact/${encodeURIComponent(artifact.id)}`, '_blank')
    if (opened) (opened as unknown as Window).opener = null
  }

  const downloadVersion = async (a: SessionArtifact, v: PanelArtifactVersion) => {
    try {
      const headers: Record<string, string> = {}
      if (shell.token) headers['Authorization'] = `Bearer ${shell.token}`
      const appArtifactUrl = `${shell.apiBaseUrl}/api/r3/app-artifacts/${encodeURIComponent(a.id)}/versions/${v.version}/download`
      if (a.type !== 'app-artifact' && !v.bodyR2Key) return
      const url = a.type === 'app-artifact'
        ? appArtifactUrl
        : `${shell.apiBaseUrl}/api/r3/artifact-body?key=${encodeURIComponent(v.bodyR2Key!)}`
      const res = await fetch(url, { credentials: 'include', headers })
      if (!res.ok) throw new Error(`Download failed (${res.status})`)
      const blob = a.type === 'app-artifact'
        ? await res.blob()
        : new Blob([await res.text()], { type: 'application/json;charset=utf-8' })
      const urlObj = URL.createObjectURL(blob)
      const el = document.createElement('a')
      el.href = urlObj
      el.download = `${a.title.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '')}-v${v.version}${typeDef(a.type).ext}`
      document.body.appendChild(el); el.click(); document.body.removeChild(el)
      setTimeout(() => URL.revokeObjectURL(urlObj), 1000)
      setToast(`Downloading “${a.title}” v${v.version}…`)
    } catch { setToast('Download failed.') }
  }

  const extend = async (a: SessionArtifact, v: PanelArtifactVersion, addDays: number) => {
    const current = v.retentionDays ?? DEFAULT_RETENTION_DAYS
    try {
      const next = await extendRetention(shell.apiBaseUrl, shell.token, a.id, v.version, Math.min(MAX_RETENTION_DAYS, current + addDays), authFetch as typeof fetch)
      setArtifacts(prev => prev.map(art => art.id !== a.id ? art : {
        ...art, versions: art.versions.map(ver => ver.version === v.version ? { ...ver, retentionDays: next } : ver),
      }))
      setToast(`Retention extended to ${next} days.`)
    } catch { setToast('Could not extend retention.') }
  }

  const doDelete = async () => {
    const a = confirmDelete
    setConfirmDelete(null)
    if (!a) return
    setArtifacts(prev => prev.filter(x => x.id !== a.id))
    try { await deleteArtifact(shell.apiBaseUrl, shell.token, a.id, authFetch as typeof fetch); setToast(`Deleted “${a.title}”.`) }
    catch { setToast('Delete failed — restoring.'); void reload() }
  }

  const handlers: ActionHandlers = {
    onOpenInChat: openInChat, onDownload: downloadVersion, onExtend: extend, onDelete: setConfirmDelete,
    onOpen: openArtifact,
  }

  return (
    <Box sx={{
      width: '100%',
      minHeight: '100%', px: 4, py: 5,
      maxWidth: 1080, mx: 'auto',
    }}>
      <Typography variant="h3" sx={SERIF_HEADING}>Artifacts</Typography>
      <Typography sx={{ color: 'text.secondary', mt: 0.5, mb: 3, maxWidth: 720 }}>
        Everything Ros has made for you that outlives a single chat — including uploadable App Artifacts, surveys, tab plans, datasets, documents and more.
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: SHOW_TYPE_FILTERS ? 1.5 : 3, flexWrap: 'wrap' }}>
        <TextField
          size="small" placeholder="Search artifacts" value={query} onChange={e => setQuery(e.target.value)}
          sx={{ width: 300 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
        <Box sx={{ flex: 1 }} />
        {appArtifactsEnabled && <>
          <input
            ref={importInputRef}
            type="file"
            accept=".zip,application/zip"
            hidden
            onChange={importAppArtifact}
          />
          <Button
            size="small"
            variant="outlined"
            startIcon={importing ? <CircularProgress size={14} /> : <UploadIcon fontSize="small" />}
            disabled={importing}
            onClick={openAppArtifactUpload}
          >
            {importing ? 'Uploading…' : 'Upload App Artifact'}
          </Button>
        </>}
        <Box sx={{ display: 'flex', border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
          <Tooltip title="Table view"><IconButton size="small" onClick={() => setLayout('table')} sx={{ borderRadius: 0, color: layout === 'table' ? 'primary.main' : 'text.disabled', bgcolor: layout === 'table' ? 'rgba(0,143,168,0.08)' : 'transparent' }}><TableRowsOutlinedIcon fontSize="small" /></IconButton></Tooltip>
          <Tooltip title="Card view"><IconButton size="small" onClick={() => setLayout('cards')} sx={{ borderRadius: 0, color: layout === 'cards' ? 'primary.main' : 'text.disabled', bgcolor: layout === 'cards' ? 'rgba(0,143,168,0.08)' : 'transparent' }}><GridViewOutlinedIcon fontSize="small" /></IconButton></Tooltip>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>Sort</Typography>
          <Select size="small" value={sort} onChange={e => setSort(e.target.value as ArtSortId)} sx={{ borderRadius: 2, fontSize: '0.85rem', minWidth: 170 }}>
            {ART_SORTS.map(s => <MenuItem key={s.id} value={s.id} sx={{ fontSize: '0.85rem' }}>{s.label}</MenuItem>)}
          </Select>
        </Box>
      </Box>

      <Dialog
        open={builderDialogOpen}
        onClose={() => { if (!importing) setBuilderDialogOpen(false) }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Upload App Artifact</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{mb: 2}}>
            Upload a finished App Artifact ZIP, or download the builder skill if you need to convert an existing HTML dashboard first.
          </DialogContentText>
          <AppArtifactBuilderCard
            metadata={builderSkill}
            loading={builderLoading}
            downloading={builderDownloading}
            error={builderError}
            onDownload={() => { void downloadBuilderSkill() }}
            onUpload={() => importInputRef.current?.click()}
            onRetry={() => { void loadBuilderSkill() }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBuilderDialogOpen(false)} disabled={importing}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Type filter chips hidden for now. Filtering logic (typeFilter/setTypeFilter/presentTypes)
          is retained; flip SHOW_TYPE_FILTERS to true to bring the chip row back. */}
      {SHOW_TYPE_FILTERS && (
        <Box sx={{ display: 'flex', gap: 1, mb: 2.5, flexWrap: 'wrap' }}>
          <FilterChip active={typeFilter === 'all'} label={`All (${artifacts.length})`} onClick={() => setTypeFilter('all')} />
          {presentTypes.map(t => <FilterChip key={t} active={typeFilter === t} label={`${typeDef(t).label} (${counts[t]})`} onClick={() => setTypeFilter(t)} />)}
        </Box>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress sx={{ color: 'primary.main' }} /></Box>
      ) : visible.length === 0 ? (
        <Paper variant="outlined" sx={{ borderRadius: 2, p: 6, textAlign: 'center' }}>
          {/* TypeTile is a fixed-size flex box (block-level), so textAlign:center on the Paper
              doesn't center it. Wrap in a centering flex row. */}
          <Box sx={{ display: 'flex', justifyContent: 'center' }}><TypeTile type="survey" size={48} /></Box>
          <Typography sx={{ fontWeight: 600, mt: 1.5 }}>{artifacts.length === 0 ? 'Nothing here yet' : 'No artifacts match'}</Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem', mt: 0.5 }}>
            {artifacts.length === 0 ? "Anything Ros generates that's worth keeping shows up here automatically." : 'Try a different search or filter.'}
          </Typography>
        </Paper>
      ) : layout === 'table' ? (
        <ArtifactsTable artifacts={visible} expanded={expanded} onToggle={toggleExpand} showMgmt={artifactsMgmtEnabled} {...handlers} />
      ) : (
        <ArtifactsCards artifacts={visible} expanded={expanded} onToggle={toggleExpand} showMgmt={artifactsMgmtEnabled} {...handlers} />
      )}

      <Typography variant="caption" sx={{ display: 'block', color: 'text.disabled', mt: 2 }}>
        Artifacts are private by default. Retention controls are shown where the artifact type supports them.
      </Typography>

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle sx={SERIF_HEADING}>Delete this artifact?</DialogTitle>
        <DialogContent>
          <DialogContentText>“{confirmDelete?.title}” and all its versions will be removed. This can't be undone.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={doDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={TOAST_MS} onClose={() => setToast(null)} message={toast ?? ''} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  )
}

// Master switch for the type-filter chip row. Off for now (hidden from the UI) while the filtering
// logic stays intact; set to true to re-expose the chips.
const SHOW_TYPE_FILTERS = false

function FilterChip({ active, label, onClick }: { active: boolean; label: string; onClick: () => void }) {
  return (
    <Box onClick={onClick} sx={{
      px: 1.5, py: 0.5, borderRadius: 999, cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600,
      border: '1px solid', userSelect: 'none',
      borderColor: active ? 'primary.main' : 'divider',
      color: active ? 'primary.main' : 'text.secondary',
      bgcolor: active ? 'rgba(0,143,168,0.08)' : 'transparent',
      '&:hover': { bgcolor: active ? 'rgba(0,143,168,0.12)' : 'action.hover' },
    }}>{label}</Box>
  )
}

interface ActionHandlers {
  onOpenInChat: (artifact: SessionArtifact, version?: PanelArtifactVersion) => void
  onDownload: (a: SessionArtifact, v: PanelArtifactVersion) => void
  onExtend: (a: SessionArtifact, v: PanelArtifactVersion, addDays: number) => void
  onDelete: (a: SessionArtifact) => void
  onOpen: (a: SessionArtifact) => void
}

function RowActions({ a, v, handlers, isParent, showMgmt }: { a: SessionArtifact; v: PanelArtifactVersion; handlers: ActionHandlers; isParent: boolean; showMgmt: boolean }) {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null)
  const r = retentionFor(v)
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }} onClick={e => e.stopPropagation()}>
      {isParent && <Tooltip title="Open artifact in new tab"><IconButton size="small" onClick={() => handlers.onOpen(a)} sx={{ color: 'text.secondary' }}><OpenInNewOutlinedIcon fontSize="small" /></IconButton></Tooltip>}
      <Tooltip title={v.sessionId ? 'Open chat' : 'Start a chat with this artifact'}>
        <IconButton size="small" onClick={() => handlers.onOpenInChat(a, v)} sx={{ color: 'text.secondary' }}><ForumOutlinedIcon fontSize="small" /></IconButton>
      </Tooltip>
      {!showMgmt && a.type !== 'markdown' && <Tooltip title="Download"><IconButton size="small" onClick={() => handlers.onDownload(a, v)} sx={{ color: 'text.secondary' }}><DownloadOutlinedIcon fontSize="small" /></IconButton></Tooltip>}
      {(isParent || a.type !== 'app-artifact') && <Tooltip title="More"><IconButton size="small" onClick={e => setAnchor(e.currentTarget)} sx={{ color: 'text.secondary' }}><MoreVertIcon fontSize="small" /></IconButton></Tooltip>}
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
        {showMgmt ? (
          <>
            <Tooltip title="Coming soon" placement="right">
              <span><MenuItem disabled><EditOutlinedIcon fontSize="small" sx={{ mr: 1 }} />Rename</MenuItem></span>
            </Tooltip>
            {isParent && <Divider />}
            {isParent && <MenuItem onClick={() => { setAnchor(null); handlers.onDelete(a) }} sx={{ color: 'error.main' }}><DeleteOutlineIcon fontSize="small" sx={{ mr: 1 }} />Delete artifact</MenuItem>}
          </>
        ) : a.type === 'app-artifact' ? (
          isParent ? <MenuItem onClick={() => { setAnchor(null); handlers.onDelete(a) }} sx={{ color: 'error.main' }}><DeleteOutlineIcon fontSize="small" sx={{ mr: 1 }} />Delete artifact</MenuItem> : null
        ) : (
          <>
            {!r.atMax && <MenuItem onClick={() => { setAnchor(null); handlers.onExtend(a, v, 30) }}><AutorenewIcon fontSize="small" sx={{ mr: 1 }} />Extend 30 days</MenuItem>}
            {!r.atMax && <MenuItem onClick={() => { setAnchor(null); handlers.onExtend(a, v, 90) }}><AutorenewIcon fontSize="small" sx={{ mr: 1 }} />Extend 90 days</MenuItem>}
            {!r.atMax && <MenuItem onClick={() => { setAnchor(null); handlers.onExtend(a, v, MAX_RETENTION_DAYS) }}><AutorenewIcon fontSize="small" sx={{ mr: 1 }} />Extend to max</MenuItem>}
            {isParent && <MenuItem onClick={() => { setAnchor(null); handlers.onDelete(a) }} sx={{ color: 'error.main' }}><DeleteOutlineIcon fontSize="small" sx={{ mr: 1 }} />Delete artifact</MenuItem>}
          </>
        )}
      </Menu>
    </Box>
  )
}

interface ListProps extends ActionHandlers {
  artifacts: SessionArtifact[]
  expanded: Set<string>
  onToggle: (id: string) => void
  showMgmt: boolean
}

const GRID_MGMT = 'minmax(220px, 1fr) 175px 165px 90px 130px'
const GRID_BASE = '1fr 110px 150px 170px 130px'

function ArtifactsTable({ artifacts, expanded, onToggle, showMgmt, ...handlers }: ListProps) {
  const grid = showMgmt ? GRID_MGMT : GRID_BASE
  const headers = showMgmt
    ? ['Name', 'Source chat', 'Last changed', 'Links', '']
    : ['Name', 'Type', 'Modified', 'Retention', '']
  return (
    <Paper variant="outlined" sx={{ borderRadius: 2.5, overflow: 'auto' }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: grid, minWidth: showMgmt ? 780 : 'auto', px: 2.5, py: 1, bgcolor: 'action.hover', borderBottom: '1px solid', borderColor: 'divider' }}>
        {headers.map((h, i) => (
          <Typography key={i} sx={{ fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'text.secondary', fontSize: '0.68rem' }}>{h}</Typography>
        ))}
      </Box>
      {artifacts.map((a, i) => {
        const lv = latestVersion(a)
        const def = typeDef(a.type)
        const r = retentionFor(lv)
        const multi = a.versions.length > 1
        const isOpen = expanded.has(a.id)
        const subtitle = showMgmt
          ? `${def.label}   ${def.ext} · v${lv.version}`
          : `${multi ? `${a.versions.length} versions` : `v${lv.version}`}${lv.source ? ` · ${lv.source}` : ''}${lv.sizeBytes != null ? ` · ${formatBytes(lv.sizeBytes)}` : ''}`
        const onRowClick = () => handlers.onOpen(a)
        return (
          <Box key={a.id}>
            <Box
              onClick={onRowClick}
              sx={{
                display: 'grid', gridTemplateColumns: grid, alignItems: 'center', minWidth: showMgmt ? 780 : 'auto',
                px: 2.5, py: 1.4, cursor: 'pointer', borderTop: i === 0 ? 'none' : '1px solid', borderColor: 'divider',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                {!showMgmt && (multi ? (
                  <IconButton
                    size="small"
                    aria-label={isOpen ? 'Collapse versions' : 'Expand versions'}
                    onClick={event => { event.stopPropagation(); onToggle(a.id) }}
                    sx={{ width: 18, height: 18, color: 'text.disabled' }}
                  >
                    {isOpen ? <ExpandMoreIcon sx={{ fontSize: 18 }} /> : <ChevronRightIcon sx={{ fontSize: 18 }} />}
                  </IconButton>
                ) : <Box sx={{ width: 18 }} />)}
                <TypeTile type={a.type} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography noWrap sx={{ fontWeight: 600, fontSize: '0.9rem' }}>{a.title}</Typography>
                  <Typography noWrap variant="caption" sx={{ color: 'text.secondary' }}>
                    {subtitle}
                  </Typography>
                </Box>
              </Box>
              {!showMgmt && <Box><TypeChip type={a.type} /></Box>}
              {showMgmt && (a.sourceChatTitle ? (
                <Typography noWrap variant="caption" sx={{ color: 'text.secondary' }}>
                  {a.sourceChatTitle}
                </Typography>
              ) : (
                <Typography variant="caption" sx={{ color: 'text.disabled' }}>—</Typography>
              ))}
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>{relativeModified(lv.publishedAt)}</Typography>
              {showMgmt && <Typography variant="caption" sx={{ color: 'text.disabled' }}>-</Typography>}
              {!showMgmt && <Box><RetentionChip r={r} /></Box>}
              <Box sx={{ justifySelf: 'end' }}><RowActions a={a} v={lv} handlers={handlers} isParent showMgmt={showMgmt} /></Box>
            </Box>
            {!showMgmt && multi && isOpen && a.versions.filter(v => v.version !== lv.version).sort((x, y) => y.version - x.version).map(v => {
              const vr = retentionFor(v)
              return (
                <Box key={v.version} sx={{ display: 'grid', gridTemplateColumns: grid, alignItems: 'center', px: 2.5, py: 1, pl: 6.5, bgcolor: 'rgba(0,0,0,0.015)', borderTop: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>v{v.version}</Typography>
                  <Box />
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>{relativeModified(v.publishedAt)}</Typography>
                  {showMgmt && <Box />}
                  {!showMgmt && <Box><RetentionChip r={vr} /></Box>}
                  <Box sx={{ justifySelf: 'end' }}><RowActions a={a} v={v} handlers={handlers} isParent={false} showMgmt={showMgmt} /></Box>
                </Box>
              )
            })}
          </Box>
        )
      })}
    </Paper>
  )
}

function ArtifactsCards({ artifacts, expanded, onToggle, showMgmt, ...handlers }: ListProps) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 2 }}>
      {artifacts.map(a => {
        const lv = latestVersion(a)
        const def = typeDef(a.type)
        const r = retentionFor(lv)
        const multi = a.versions.length > 1
        const isOpen = expanded.has(a.id)
        return (
          <Paper
            key={a.id}
            variant="outlined"
            onClick={() => handlers.onOpen(a)}
            sx={{ borderRadius: 2, p: 2, display: 'flex', flexDirection: 'column', gap: 1, cursor: 'pointer', '&:hover': { borderColor: 'primary.main' } }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <TypeTile type={a.type} size={40} />
              <RowActions a={a} v={lv} handlers={handlers} isParent showMgmt={showMgmt} />
            </Box>
            <Box>
              <TypeChip type={a.type} />
              <Typography sx={{ fontWeight: 600, fontSize: '0.92rem', mt: 0.75 }}>{a.title}</Typography>
              {showMgmt ? (
                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>{def.label} · {def.ext} · v{lv.version}</Typography>
              ) : (
                lv.source && <Typography variant="caption" sx={{ color: 'text.secondary' }}>{lv.source}{lv.sizeBytes != null ? ` · ${formatBytes(lv.sizeBytes)}` : ''}</Typography>
              )}
              <Typography variant="caption" sx={{ display: 'block', color: a.sourceChatTitle ? 'text.secondary' : 'text.disabled' }}>
                {a.sourceChatTitle ?? '—'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 'auto' }}>
              <Typography variant="caption" sx={{ color: 'text.disabled' }}>Modified {relativeModified(lv.publishedAt)}</Typography>
              <RetentionChip r={r} />
            </Box>
            <Typography variant="caption" sx={{ color: 'text.disabled' }}>-</Typography>
            {!showMgmt && multi && (
              <Box>
                <Box onClick={event => { event.stopPropagation(); onToggle(a.id) }} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', color: 'primary.main', fontSize: '0.75rem', fontWeight: 600, mt: 0.5 }}>
                  {isOpen ? <ExpandMoreIcon sx={{ fontSize: 16 }} /> : <ChevronRightIcon sx={{ fontSize: 16 }} />} {a.versions.length} versions
                </Box>
                {isOpen && a.versions.filter(v => v.version !== lv.version).sort((x, y) => y.version - x.version).map(v => {
                  const vr = retentionFor(v)
                  return (
                    <Box key={v.version} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pl: 1.5, py: 0.5, borderTop: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>v{v.version} · {relativeModified(v.publishedAt)}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><RetentionChip r={vr} /><RowActions a={a} v={v} handlers={handlers} isParent={false} showMgmt={showMgmt} /></Box>
                    </Box>
                  )
                })}
              </Box>
            )}
          </Paper>
        )
      })}
    </Box>
  )
}
